import { useLocalStorage } from './useLocalStorage';
import { useCallback, useMemo } from 'react';

// Maps column IDs to backend metric names
// Some columns are always calculated (core), others are optional
const COLUMN_TO_METRIC: Record<string, string | null> = {
  // Core columns - always calculated, no metric needed
  ticker: null,
  totalReturn: null,
  cagr: null,
  finalValue: null,  // This is the 100% DRIP value from core calculation
  dividendsReceived: null,
  // Optional metrics
  underlyingTicker: 'underlying_ticker',
  inceptionDate: 'inception_date',
  finalValue0Percent: 'partial_reinvestment',
  finalValue50Percent: 'partial_reinvestment',
  finalValue100Percent: 'partial_reinvestment',
  flatThresholdPercent: 'flat_threshold',
  dividendYield: 'dividend_yield',
  dividendGrowthRatePercent: 'dividend_growth_rate',
  maxDrawdownPercent: 'max_drawdown',
  volatilityPercent: 'volatility',
  startPrice: 'share_price',
  endPrice: 'share_price',
  priceChangeDollars: 'share_price',
  priceChangePercent: 'share_price',
};

/**
 * Convert visible column IDs to backend metric names
 * Returns undefined if no optional metrics are needed (to calculate all)
 */
export function columnsToMetrics(columnIds: string[]): string[] {
  const metrics = new Set<string>();
  for (const colId of columnIds) {
    const metric = COLUMN_TO_METRIC[colId];
    if (metric) {
      metrics.add(metric);
    }
  }
  return Array.from(metrics);
}

export interface ColumnDefinition {
  id: string;
  label: string;
  group: string;
}

export const ALL_COLUMNS: ColumnDefinition[] = [
  // Core columns
  { id: 'ticker', label: 'Ticker', group: 'Basic' },
  { id: 'underlyingTicker', label: 'Underlying', group: 'Basic' },
  { id: 'inceptionDate', label: 'Inception Date', group: 'Info' },
  { id: 'totalReturn', label: 'Total Return (%)', group: 'Returns' },
  { id: 'cagr', label: 'CAGR (%)', group: 'Returns' },
  // Scenario columns - IDs must match ReturnsTable
  { id: 'finalValue0Percent', label: '0% Reinvested', group: 'Scenarios' },
  { id: 'finalValue50Percent', label: '50% Reinvested', group: 'Scenarios' },
  { id: 'finalValue100Percent', label: '100% Reinvested', group: 'Scenarios' },
  { id: 'flatThresholdPercent', label: 'Flat Threshold %', group: 'Analysis' },
  // Dividend columns
  { id: 'dividendYield', label: 'Dividend Yield', group: 'Dividends' },
  { id: 'dividendsReceived', label: 'Dividends Received', group: 'Dividends' },
  { id: 'dividendGrowthRatePercent', label: 'Dividend Growth', group: 'Dividends' },
  // Risk columns
  { id: 'maxDrawdownPercent', label: 'Max Drawdown', group: 'Risk' },
  { id: 'volatilityPercent', label: 'Volatility', group: 'Risk' },
  // Share price columns
  { id: 'startPrice', label: 'Start Price', group: 'Price' },
  { id: 'endPrice', label: 'End Price', group: 'Price' },
  { id: 'priceChangeDollars', label: 'Price Change ($)', group: 'Price' },
  { id: 'priceChangePercent', label: 'Price Change (%)', group: 'Price' },
];

const DEFAULT_VISIBLE = ['ticker', 'underlyingTicker', 'totalReturn', 'cagr', 'dividendYield', 'dividendsReceived'];

export interface UseColumnVisibilityOptions {
  /** External visible columns state (for use with useUserState) */
  visibleColumns?: string[];
  /** External setter (for use with useUserState) */
  setVisibleColumns?: (value: string[] | ((prev: string[]) => string[])) => void;
}

export function useColumnVisibility(options?: UseColumnVisibilityOptions) {
  // Use local storage as fallback if external state not provided
  const [localVisibleColumns, setLocalVisibleColumns] = useLocalStorage<string[]>(
    'calculator_visible_columns',
    DEFAULT_VISIBLE
  );

  // Use external state if provided, otherwise use local storage
  const visibleColumns = options?.visibleColumns ?? localVisibleColumns;
  const setVisibleColumns = options?.setVisibleColumns ?? setLocalVisibleColumns;

  const toggleColumn = useCallback((columnId: string) => {
    setVisibleColumns(prev =>
      prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  }, [setVisibleColumns]);

  const toggleAll = useCallback((show: boolean) => {
    setVisibleColumns(show ? ALL_COLUMNS.map(c => c.id) : ['ticker']);
  }, [setVisibleColumns]);

  const isVisible = useCallback((columnId: string) =>
    visibleColumns.includes(columnId), [visibleColumns]);

  // Compute the requested metrics based on visible columns
  const requestedMetrics = useMemo(() =>
    columnsToMetrics(visibleColumns), [visibleColumns]);

  return {
    visibleColumns,
    toggleColumn,
    toggleAll,
    isVisible,
    allColumns: ALL_COLUMNS,
    requestedMetrics,
  };
}
